<template>
  <PartnersContainer>
    <template #header>
      <div :class="$style.header">
        <h1 :class="[$style.title, 'h3']">{{ t('partners.title') }}</h1>

        <div :class="$style.headerCaption">
          {{ t('partners.caption') }}
        </div>
      </div>
    </template>

    <template #nav>
      <UiTimeLine
        v-model="activeCategory"
        :lines="nav"
        :class="$style.navigation"
      />
    </template>

    <template #list>
      <transition-group name="fade">
        <BlogSinglePostSkeleton
          v-if="categoriesStatus === 'pending' || partnersStatus === 'pending'"
          v-for="i of 9"
        />
      </transition-group>
      <transition-group name="fade">
        <PartnersSinglePost
          v-if="categoriesStatus === 'success' && partnersStatus === 'success'"
          v-for="(post, index) of partners"
          :key="index"
          :title="post?.title.rendered"
          :image="post?.acf?.partners_image || '/images/topPost.webp'"
          :topic="categories?.find((cat: any) => cat.term_id === activeCategory)?.name"
          :caption="post?.excerpt.rendered"
          :_id="post?.id"
        />
      </transition-group>
    </template>

    <template #pagination>
      <UiPagination
        v-if="partnersStatus === 'success'"
        v-model="page"
        :pages="totalPages"
      />
    </template>
  </PartnersContainer>
</template>

<script setup lang="ts">
import type { TimeLineItem } from '@/components/ui/TimeLine/index.js';
import type { SinglePostParams } from '@/components/Partners/index.js';
import { eventTracker, AnalyticsEventKey } from '~/utils/analyticServices';

const { locale, t } = useI18n();

useHead({
  title: t('meta.partners.title'),
});

const page = ref<number>(1);
const totalPages = useState('totalPages', () => 0);
const activeCategory = useState('activeCategory', () => '');

const { data: categories, status: categoriesStatus } = await useAsyncData(
  'partners-categories',
  async () => {
    const response = await fetch(`https://wp.0detect.com/wp-json/custom/v1/partners_category?meta_key=lang&meta_value=${locale.value}`);
    const output = await response.json();
    activeCategory.value = output?.[0]?.term_id || '';
    return output;
  },
  {
    watch: [locale],
    server: true,
    lazy: false,
    default: () => [],
  },
);

const { data: partners, status: partnersStatus } = await useAsyncData(
  'partners-list',
  () => getPartners(page.value, activeCategory.value),
  {
    watch: [page, activeCategory, locale],
    server: true,
    lazy: false,
    default: () => [],
  },
);

watch(locale, () => {
  page.value = 1;
});

async function getPartners(page: number, cat: string) {
  const response = await fetch(`https://wp.0detect.com/wp-json/wp/v2/partners?partners_category=${cat}&per_page=12&page=${page}`);
  const totalPagesHeader = response.headers.get('X-WP-TotalPages');
  if (totalPagesHeader) {
    totalPages.value = parseInt(totalPagesHeader, 10);
  }
  return await response.json();
}

const nav = computed<TimeLineItem[]>(() => {
  return categories.value?.map((cat: any) => ({
    label: cat.name,
    value: cat.term_id,
  })) ?? [];
});

onMounted(() => {
  eventTracker(AnalyticsEventKey.PartnersPageViewed, {
    platform: 'WebSite',
  });
});

</script>

<style lang="scss" module>
  .title {
    margin: 0;
    color: #121E29;
  }

  .header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 15px;
    margin: 0 0 40px 0;

    &Caption {
      max-width: 460px;
    }
  }
</style>